// src/components/CustomSwitch.tsx
import { memo, CSSProperties } from 'react';
import { useMantineTheme, useMantineColorScheme } from '@mantine/core';

interface CustomSwitchProps {
	checked: boolean;
	onChange: () => void;
	label: string;
	isOverdue: boolean;
}

const CustomSwitch = memo(function CustomSwitch({
	checked,
	onChange,
	label,
	isOverdue,
}: CustomSwitchProps) {
	const theme = useMantineTheme();
	const { colorScheme } = useMantineColorScheme();
	const isDark = colorScheme === 'dark';

	const switchStyles: CSSProperties = {
		display: 'flex',
		alignItems: 'center',
		cursor: 'pointer',
		transform: 'scale(1.2)',
		transformOrigin: 'left center',
		userSelect: 'none',
	};

	const labelContainerStyles: CSSProperties = {
		width: '5rem',
		height: '2rem',
		position: 'relative',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
	};

	const trackStyles: CSSProperties = {
		width: '100%',
		height: '100%',
		backgroundColor: checked
			? theme.colors.teal[6]
			: isOverdue
			? theme.colors.red[6]
			: isDark
			? theme.colors.dark[4]
			: theme.colors.gray[3],
		borderRadius: '1rem',
		position: 'absolute',
		top: 0,
		left: 0,
		transition: 'background-color 0.2s ease',
	};

	const thumbStyles: CSSProperties = {
		width: '1.6rem',
		height: '1.6rem',
		backgroundColor: isDark ? theme.colors.dark[0] : theme.white,
		boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
		borderRadius: '50%',
		position: 'absolute',
		top: '0.2rem',
		left: checked ? 'calc(100% - 1.8rem)' : '0.2rem',
		transition: 'left 0.2s ease',
	};

	const baseTextStyles: CSSProperties = {
		position: 'absolute',
		zIndex: 1,
		fontSize: '0.875rem',
		fontWeight: 600,
		color: checked
			? theme.white
			: isOverdue
			? theme.white
			: isDark
			? theme.colors.dark[0]
			: theme.colors.dark[3],
		transition: 'opacity 0.2s ease',
	};

	const paidTextStyles: CSSProperties = {
		...baseTextStyles,
		left: '0.5rem',
		opacity: checked ? 1 : 0,
	};

	const dateTextStyles: CSSProperties = {
		...baseTextStyles,
		right: '0.5rem',
		opacity: checked ? 0 : 1,
	};

	return (
		<div style={switchStyles} onClick={onChange}>
			<div style={labelContainerStyles}>
				<div style={trackStyles} />
				<div style={thumbStyles} />
				<span style={paidTextStyles}>PAID</span>
				<span style={dateTextStyles}>{label}</span>
			</div>
		</div>
	);
});

export default CustomSwitch;
